import React, { useState, useCallback } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { Helmet } from 'react-helmet';

import { mutateBpicLocationFormatToInputFormat } from '@/lib/utils';
import { AccountOptionModel, FormOption, TimezoneOptionModel } from '@/lib/models';
import { accountService } from '@/lib/services';
import { useAccount, useAlert, useCanManageBookingsUrl, useHandleError } from '@/hooks';
import { AsyncPage, LoadingButton, LocationTypeahead, Select } from '@/components';

export const Profile = () => {
	const handleError = useHandleError();
	const { showAlert } = useAlert();
	const { account, setAccount } = useAccount();
	const canManageBookingsUrl = useCanManageBookingsUrl();

	const [accountLevelOptions, setAccountLevelOptions] = useState<AccountOptionModel[]>([]);
	const [accountFunctionOptions, setAccountFunctionOptions] = useState<FormOption[]>([]);
	const [timezoneOptions, setTimezoneOptions] = useState<TimezoneOptionModel[]>([]);

	const [profileFormEnabled, setProfileFormEnabled] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [profileForm, setProfileForm] = useState({
		name: '',
		title: '',
		userLevelTagId: '',
		userFunctionTagIds: [] as FormOption[],
		location: '',
		mobileNumber: '',
		...(canManageBookingsUrl && { bookingsUrl: '' }),
		timeZone: '',
	});
	const fetchData = useCallback(async () => {
		const [levelOptionsResponse, functionOptionsResponse, timezonesResponse, accountResponse] = await Promise.all([
			accountService.getAccountOptions('USER_LEVEL').fetch(),
			accountService.getAccountOptions('USER_FUNCTION').fetch(),
			accountService.getTimezones().fetch(),
			accountService.getCurrentAccount().fetch(),
		]);

		setAccount(accountResponse.account);
		setAccountLevelOptions(levelOptionsResponse.options);
		setTimezoneOptions(timezonesResponse.timezones);
		setAccountFunctionOptions(
			functionOptionsResponse.options.map((accountOption) => ({
				id: accountOption.tagId,
				display: accountOption.name,
			}))
		);
	}, [setAccount]);

	const handleEditProfileButtonClick = () => {
		if (!account) {
			return;
		}

		setProfileFormEnabled(true);
		setProfileForm({
			name: account.name ?? '',
			title: account.title ?? '',
			userLevelTagId: account.userLevelTagId ?? '',
			userFunctionTagIds: accountFunctionOptions.filter(({ id }) =>
				(account.userFunctionTagIds ?? []).includes(id)
			),
			location: account.location ?? '',
			mobileNumber: account.mobileNumber ?? '',
			...(canManageBookingsUrl && { bookingsUrl: account.bookingsUrl ?? '' }),
			timeZone: account.timeZone.id,
		});
	};

	const handleInputChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
		setProfileForm((previousValue) => {
			return {
				...previousValue,
				[currentTarget.name]: currentTarget.value,
			};
		});
	};

	const cancelChanges = () => {
		setProfileFormEnabled(false);
	};

	const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsLoading(true);

		try {
			if (!account) {
				throw new Error('account is undefined.');
			}

			const accountResponse = await accountService
				.updateCurrentAccount(account.accountId, {
					...profileForm,
					userFunctionTagIds: profileForm.userFunctionTagIds.map((tag) => tag.id),
				})
				.fetch();

			setAccount(accountResponse.account);

			showAlert({
				variant: 'success',
				children: <p className="mb-0 text-white">Your account has been updated.</p>,
			});
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
			setProfileFormEnabled(false);
		}
	};

	return (
		<AsyncPage fetchData={fetchData}>
			<Helmet>
				<title>Hackett Connect - Account - Profile</title>
			</Helmet>
			<Row>
				<Col>
					<Card>
						<Card.Header>
							<Card.Title className="large">Profile</Card.Title>
						</Card.Header>
						<Card.Body>
							<Form onSubmit={handleFormSubmit}>
								<fieldset disabled={isLoading}>
									{profileFormEnabled ? (
										<>
											<Row>
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>Full Name</Form.Label>
														<Form.Control
															type="text"
															name="name"
															value={profileForm.name}
															onChange={handleInputChange}
															required
														/>
													</Form.Group>
												</Col>
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>Title</Form.Label>
														<Form.Control
															type="text"
															name="title"
															value={profileForm.title}
															onChange={handleInputChange}
															required
														/>
													</Form.Group>
												</Col>
											</Row>
											<Row>
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>Level</Form.Label>
														<Select
															value={profileForm.userLevelTagId}
															onChange={(event) =>
																setProfileForm({
																	...profileForm,
																	userLevelTagId: event.target.value,
																})
															}
														>
															{accountLevelOptions.map((option) => (
																<option key={option.tagId} value={option.tagId}>
																	{option.name}
																</option>
															))}
														</Select>
													</Form.Group>
												</Col>
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>Function</Form.Label>
														<Typeahead
															id="typeahead--jobFunction"
															labelKey="display"
															multiple
															selected={profileForm.userFunctionTagIds}
															onChange={(selected) => {
																setProfileForm({
																	...profileForm,
																	userFunctionTagIds: selected as FormOption[],
																});
															}}
															options={accountFunctionOptions}
															placeholder="Select Job Function"
														/>
													</Form.Group>
												</Col>
											</Row>
											<Row>
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>
															Location{' '}
															<span className="text-muted font-italic font-weight-regular">
																- Optional
															</span>
														</Form.Label>
														<LocationTypeahead
															id="profile-location"
															value={profileForm.location}
															onChange={(location) =>
																setProfileForm((previousValue) => ({
																	...previousValue,
																	location,
																}))
															}
														/>
													</Form.Group>
												</Col>
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>
															Office Phone{' '}
															<span className="text-muted font-italic font-weight-regular">
																- Optional
															</span>
														</Form.Label>
														<Form.Control
															type="text"
															name="mobileNumber"
															value={profileForm.mobileNumber}
															onChange={handleInputChange}
														/>
													</Form.Group>
												</Col>
											</Row>

											<Row>
												{canManageBookingsUrl && (
													<Col lg={6}>
														<Form.Group className="mb-5">
															<Form.Label>
																Bookings URL{' '}
																<span className="text-muted font-italic font-weight-regular">
																	- Optional
																</span>
															</Form.Label>
															<Form.Control
																type="text"
																name="bookingsUrl"
																value={profileForm.bookingsUrl}
																onChange={handleInputChange}
															/>
														</Form.Group>
													</Col>
												)}
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>Timezone</Form.Label>
														<Select
															name="timeZone"
															value={profileForm.timeZone}
															onChange={handleInputChange}
														>
															{timezoneOptions.map((option) => (
																<option key={option.id} value={option.id}>
																	{option.description}
																</option>
															))}
														</Select>
													</Form.Group>
												</Col>
											</Row>
										</>
									) : (
										<>
											<Row>
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>Full Name</Form.Label>
														<p className="mb-0">{account?.name}</p>
													</Form.Group>
												</Col>
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>Title</Form.Label>
														<p className="mb-0">{account?.title}</p>
													</Form.Group>
												</Col>
											</Row>
											<Row>
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>Level</Form.Label>
														{account?.userLevelDescription ? (
															<p className="mb-0">{account?.userLevelDescription}</p>
														) : (
															<p className="mb-0 text-muted font-italic">None</p>
														)}
													</Form.Group>
												</Col>
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>Function</Form.Label>
														{account?.userFunctionDescription ? (
															<p className="mb-0">{account?.userFunctionDescription}</p>
														) : (
															<p className="mb-0 text-muted font-italic">None</p>
														)}
													</Form.Group>
												</Col>
											</Row>
											<Row>
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>
															Location{' '}
															<span className="text-muted font-italic font-weight-regular">
																- Optional
															</span>
														</Form.Label>
														{account?.location ? (
															<p className="mb-0">
																{mutateBpicLocationFormatToInputFormat(
																	account.location
																)}
															</p>
														) : (
															<p className="mb-0 text-muted font-italic">None</p>
														)}
													</Form.Group>
												</Col>
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>
															Office Phone{' '}
															<span className="text-muted font-italic font-weight-regular">
																- Optional
															</span>
														</Form.Label>
														{account?.mobileNumber ? (
															<p className="mb-0">{account?.mobileNumber}</p>
														) : (
															<p className="mb-0 text-muted font-italic">None</p>
														)}
													</Form.Group>
												</Col>
											</Row>

											<Row>
												{canManageBookingsUrl && (
													<Col lg={6}>
														<Form.Group className="mb-5">
															<Form.Label>
																Bookings URL{' '}
																<span className="text-muted font-italic font-weight-regular">
																	- Optional
																</span>
															</Form.Label>
															{account?.bookingsUrl ? (
																<p className="mb-0 text-truncate">
																	{account?.bookingsUrl}
																</p>
															) : (
																<p className="mb-0 text-muted font-italic">None</p>
															)}
														</Form.Group>
													</Col>
												)}
												<Col lg={6}>
													<Form.Group className="mb-5">
														<Form.Label>Timezone</Form.Label>
														{account?.timeZone ? (
															<p className="mb-0 text-truncate">
																{account?.timeZone.description}
															</p>
														) : (
															<p className="mb-0 text-muted font-italic">None</p>
														)}
													</Form.Group>
												</Col>
											</Row>
										</>
									)}
									<Row>
										<Col>
											{profileFormEnabled ? (
												<div className="d-flex justify-content-between">
													<Button
														onClick={cancelChanges}
														variant="outline-primary"
														className="mr-2"
													>
														Cancel Changes
													</Button>
													<LoadingButton
														isLoading={isLoading}
														variant="primary"
														type="submit"
													>
														Save Changes
													</LoadingButton>
												</div>
											) : (
												<Button
													onClick={handleEditProfileButtonClick}
													variant="outline-primary"
												>
													Edit Profile
												</Button>
											)}
										</Col>
									</Row>
								</fieldset>
							</Form>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</AsyncPage>
	);
};
